import type { AuthApi } from '@/api';
import type { GiftTrialCampaign } from '@/api/getGiftTrialCampaign';

export type ApplyStatusResult = NonNullable<
  NonNullable<
    Awaited<
      ReturnType<
        (typeof AuthApi)['get:/wello/v2/auth/gift-trial/campaigns/{campaignId}/apply-status']
      >
    >['data']
  >['context']
>;

export type CampaignCtaKind =
  | 'upcoming'
  | 'closed'
  | 'login-required'
  | 'open'
  | 'applied'
  | 'blocked'
  | 'restricted'
  | 'in-progress'
  | 'ineligible'
  | 'verify-required'
  | 'marketing-consent-required';

export type CampaignCtaBand = 'recruit-dday' | 'announcement' | 'donation';

export interface CampaignCta {
  kind: CampaignCtaKind;
  label: string;
  disabled: boolean;
  band?: CampaignCtaBand;
}

interface ResolveCampaignCtaParams {
  isLogin: boolean;
  campaign: Pick<GiftTrialCampaign, 'campaignPhase'>;
  //* 웰로 앱 + 로그인일 때만 조회된다 — 없으면 공개 캠페인 단계로 판정
  applyStatus?: ApplyStatusResult['applyStatus'];
  //* BE는 모집예정이면 자격 판정 전에 UPCOMING을 돌려줘 기부 자격은 /me에서 따로 받는다
  isDonationEligible?: boolean;
}

export const resolveCampaignCta = ({
  isLogin,
  campaign,
  applyStatus,
  isDonationEligible,
}: ResolveCampaignCtaParams): CampaignCta => {
  const upcoming: CampaignCta = {
    kind: 'upcoming',
    label: '신청이 불가해요',
    disabled: true,
  };
  const closed: CampaignCta = {
    kind: 'closed',
    label: '모집이 마감됐어요',
    disabled: true,
  };
  const applicable = (kind: CampaignCtaKind): CampaignCta => ({
    kind,
    label: '체험단 신청하기',
    disabled: false,
    band: 'recruit-dday',
  });
  const unavailable = (kind: CampaignCtaKind): CampaignCta => ({
    kind,
    label: '신청이 불가해요',
    disabled: true,
  });

  if (!applyStatus) {
    if (campaign.campaignPhase === 'UPCOMING') {
      return upcoming;
    }
    if (campaign.campaignPhase !== 'RECRUITING') {
      return closed;
    }
    if (!isLogin) {
      return {
        kind: 'login-required',
        label: '로그인이 필요해요',
        disabled: false,
      };
    }

    return applicable('open');
  }

  switch (applyStatus) {
    case 'BLOCKED':
      return unavailable('blocked');
    case 'RESTRICTED':
      return unavailable('restricted');
    case 'UPCOMING':
      return isDonationEligible === false
        ? { ...upcoming, band: 'donation' }
        : upcoming;
    case 'APPLIED':
      return {
        kind: 'applied',
        label: '신청 완료',
        disabled: true,
        band: 'announcement',
      };
    case 'CLOSED':
      return closed;
    case 'IN_PROGRESS':
      return {
        kind: 'in-progress',
        label: '참여 중인 체험단이 있어요',
        disabled: true,
      };
    case 'INELIGIBLE':
      return {
        kind: 'ineligible',
        label: '기부 후 체험단 신청이 가능해요.',
        disabled: true,
        band: 'donation',
      };
    case 'VERIFY_REQUIRED':
      return applicable('verify-required');
    case 'MARKETING_CONSENT_REQUIRED':
      return applicable('marketing-consent-required');
    case 'OPEN':
      return applicable('open');
    default:
      return closed;
  }
};
